import { createRouter, Navigate } from "@tanstack/react-router"

import { ErrorLayout } from "@/components/layouts/error"
import { NoneLayout } from "@/components/layouts/none"
import { PageLoader } from "@/components/ui/PageLoader"
import { AuthClientInstance, queryClient, SpaceStorageInstance, themeManagerInstance } from "@/lib/services/instances"
import { ga4Emitter } from "@/lib/utils/analytics"
import { CloudContext } from "@/providers/CloudContext"
import { featureFlagsStore } from "@/store/featureflags"

// Import the generated route tree
import { routeTree } from "./routeTree.gen"

const context = {
  queryClient,
  spaceStorage: SpaceStorageInstance,
  authClient: AuthClientInstance,
  themeManager: themeManagerInstance
}
type RouterContext = typeof context

// Create a new router instance
const router = createRouter({
  routeTree,
  context,
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  // ? Should we have instant pending transitions? This is a UX decision.
  defaultPendingMinMs: 0,
  defaultPendingMs: 0,
  defaultViewTransition: featureFlagsStore.getState().flags["enable-view-transition"],
  scrollRestoration: true,
  // Default components
  defaultErrorComponent: error => <ErrorLayout error={error} />,
  defaultNotFoundComponent: () => <Navigate to="/" replace />,
  defaultPendingComponent: () => (
    <NoneLayout>
      <PageLoader />
    </NoneLayout>
  ),
  // Wrap the router with the CloudContext, so we can invalidate the router when the space changes.
  InnerWrap: ({ children }) => <CloudContext>{children}</CloudContext>
})

// Send pageview events when routes are resolved
router.subscribe("onResolved", r => {
  if (!r.pathChanged) return

  setTimeout(() => {
    const pageTitle = document.title

    // Only track if we have a title
    if (pageTitle) {
      ga4Emitter({ type: "PAGEVIEW", payload: { page_title: pageTitle } })
    }
  }, 0)
})

// Export the router and the context type
export { router }
export type { RouterContext }
